import { useEffect, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Check, Phone, Shield, Upload, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const LOT_LABEL = { S: "Small", M: "Medium", L: "Large" };
const FREQ_LABEL = { weekly: "Weekly", bi_weekly: "Bi-Weekly", one_time: "One-Time" };
const MAX_PHOTOS = 5;
const MAX_PHOTO_BYTES = 4 * 1024 * 1024; // 4MB each

export default function PricingBooking() {
  const [pricing, setPricing] = useState({
    S: { weekly: 35, bi_weekly: 40 },
    M: { weekly: 40, bi_weekly: 45 },
    L: { weekly: 50, bi_weekly: 55 },
  });
  const [fullDates, setFullDates] = useState([]);
  const [date, setDate] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]); // array of { name, dataUrl }

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "San Antonio",
    service: "Mowing",
    lot_size: "M",
    frequency: "weekly",
    is_veteran: false,
    notes: "",
  });

  useEffect(() => {
    api.get("/pricing").then((r) => setPricing(r.data.pricing)).catch(() => {});
    const month = new Date().toISOString().slice(0, 7);
    api
      .get(`/bookings/availability?month=${month}`)
      .then((r) => setFullDates(r.data.full_dates || []))
      .catch(() => {});
  }, []);

  const priceBase = pricing[form.lot_size]?.[form.frequency] ??
    (form.frequency === "one_time" ? (pricing[form.lot_size]?.bi_weekly ?? 0) + 10 : 0);
  const priceFinal = form.is_veteran ? Math.round(priceBase * 0.9 * 100) / 100 : priceBase;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onPhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting same file
    if (!files.length) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      toast.error(`Up to ${MAX_PHOTOS} photos only`);
      return;
    }
    const take = files.slice(0, remaining);
    const newOnes = [];
    for (const f of take) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name} is not an image`);
        continue;
      }
      if (f.size > MAX_PHOTO_BYTES) {
        toast.error(`${f.name} is over 4MB`);
        continue;
      }
      try {
        const dataUrl = await readFileAsDataUrl(f);
        newOnes.push({ name: f.name, dataUrl });
      } catch {
        toast.error(`Couldn't read ${f.name}`);
      }
    }
    if (newOnes.length) setPhotos((p) => [...p, ...newOnes]);
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Please pick a preferred date");
      return;
    }
    if (!form.name || !form.phone || !form.address) {
      toast.error("Name, phone, and address are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        email: form.email || null,
        preferred_date: date.toISOString().slice(0, 10),
        photos: photos.map((p) => p.dataUrl),
      };
      await api.post("/bookings", payload);
      toast.success("Request received! We'll call you within 24 hours.");
      setForm({
        name: "",
        phone: "",
        email: "",
        address: "",
        city: "San Antonio",
        service: "Mowing",
        lot_size: "M",
        frequency: "weekly",
        is_veteran: false,
        notes: "",
      });
      setDate(undefined);
      setPhotos([]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="pricing" data-testid="pricing-booking-section" className="py-24 md:py-32 bg-background border-t border-border">
      <div className="container-ic">
        <div className="max-w-3xl mb-16">
          <div className="overline mb-4">Transparent Pricing</div>
          <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
            Flat rates.
            <br />
            <span className="text-primary">No surprises.</span>
          </h2>
          <p className="mt-6 text-muted-foreground text-lg max-w-2xl">
            Mowing is flat-priced by lot size and frequency. All other services are
            quoted per-job — call for a free estimate. Veterans save an additional 10%.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Pricing table */}
          <div className="lg:col-span-5">
            <div className="overline mb-3 text-primary">Mowing — Flat Rates</div>
            <div className="border border-border">
              <div className="grid grid-cols-3 bg-accent/40 border-b border-border">
                <div className="p-4 overline text-muted-foreground">Lot</div>
                <div className="p-4 overline text-muted-foreground text-center">Weekly</div>
                <div className="p-4 overline text-muted-foreground text-center">Bi-Weekly</div>
              </div>
              {["S", "M", "L"].map((size) => (
                <div
                  key={size}
                  className={`grid grid-cols-3 border-b border-border last:border-b-0 ${
                    form.lot_size === size ? "bg-primary/10" : ""
                  }`}
                  data-testid={`pricing-row-${size}`}
                >
                  <div className="p-5">
                    <div className="font-display text-2xl font-bold">{size}</div>
                    <div className="text-xs text-muted-foreground uppercase tracking-widest">
                      {LOT_LABEL[size]}
                    </div>
                  </div>
                  <div className="p-5 text-center flex items-center justify-center">
                    <span className="font-display text-2xl font-bold">
                      ${pricing[size].weekly}
                    </span>
                  </div>
                  <div className="p-5 text-center flex items-center justify-center">
                    <span className="font-display text-2xl font-bold">
                      ${pricing[size].bi_weekly}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 border border-primary/40 bg-primary/5 p-5 flex items-start gap-3" data-testid="vet-discount-callout">
              <Shield className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-lg font-bold uppercase tracking-wider">
                  10% Veteran Discount
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Mowing starts at $25. Additional services quoted per-job.
                </p>
              </div>
            </div>

            <div
              className="mt-4 border border-border p-5"
              data-testid="contact-pricing-card"
            >
              <div className="overline mb-3">Other Services · Call for Pricing</div>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-foreground">
                {[
                  "Hedging",
                  "Mulching",
                  "Brush Trimming",
                  "Flower Beds",
                  "Clean-Ups",
                  "Fertilizer",
                  "Weed Control",
                ].map((s) => (
                  <li
                    key={s}
                    className="flex items-center gap-2 border-l border-primary/40 pl-3 py-1"
                  >
                    <span className="text-primary">·</span> {s}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-muted-foreground">
                Every job is different — we give a free, no-pressure quote on-site or over
                the phone.
              </p>
            </div>

            <a
              href="tel:5129480676"
              className="mt-4 flex items-center justify-between border border-border p-5 hover:border-primary transition-colors"
              data-testid="pricing-call-cta"
            >
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  Prefer to talk?
                </div>
                <div className="font-display text-2xl font-bold mt-1">512-948-0676</div>
              </div>
              <Phone className="h-6 w-6 text-primary" />
            </a>
          </div>

          {/* Booking form */}
          <div id="booking" className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="border border-border bg-card/30 p-6 md:p-10"
              data-testid="booking-form"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="overline">Book Service</div>
                  <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mt-1">
                    Request Estimate
                  </h3>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name *">
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    className="input-ic"
                    data-testid="booking-name"
                  />
                </Field>
                <Field label="Phone *">
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    className="input-ic"
                    data-testid="booking-phone"
                  />
                </Field>
                <Field label="Email">
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    className="input-ic"
                    data-testid="booking-email"
                  />
                </Field>
                <Field label="City">
                  <select
                    value={form.city}
                    onChange={(e) => set("city", e.target.value)}
                    className="input-ic"
                    data-testid="booking-city"
                  >
                    {["San Antonio", "New Braunfels", "Boerne", "Schertz", "Cibolo", "Other"].map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="md:col-span-2">
                  <Field label="Address *">
                    <input
                      type="text"
                      required
                      value={form.address}
                      onChange={(e) => set("address", e.target.value)}
                      className="input-ic"
                      placeholder="Street address"
                      data-testid="booking-address"
                    />
                  </Field>
                </div>
                <Field label="Service">
                  <select
                    value={form.service}
                    onChange={(e) => set("service", e.target.value)}
                    className="input-ic"
                    data-testid="booking-service"
                  >
                    {[
                      "Mowing",
                      "Hedging",
                      "Mulching",
                      "Brush Trimming",
                      "Flower Beds",
                      "Clean-Up",
                      "Fertilizer",
                      "Weed Control",
                      "Power Washing",
                      "Full Property Makeover",
                    ].map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Lot Size">
                  <div className="grid grid-cols-3 gap-2">
                    {["S", "M", "L"].map((s) => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => set("lot_size", s)}
                        className={`py-3 border text-sm font-semibold uppercase tracking-wider transition-colors ${
                          form.lot_size === s
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border text-muted-foreground hover:border-primary"
                        }`}
                        data-testid={`booking-lot-${s}`}
                      >
                        {s} · {LOT_LABEL[s]}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Frequency">
                  <div className="grid grid-cols-3 gap-2">
                    {Object.keys(FREQ_LABEL).map((f) => (
                      <button
                        type="button"
                        key={f}
                        onClick={() => set("frequency", f)}
                        className={`py-3 border text-xs font-semibold uppercase tracking-wider transition-colors ${
                          form.frequency === f
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border text-muted-foreground hover:border-primary"
                        }`}
                        data-testid={`booking-freq-${f}`}
                      >
                        {FREQ_LABEL[f]}
                      </button>
                    ))}
                  </div>
                </Field>

                <div className="md:col-span-2">
                  <Field label="Notes">
                    <textarea
                      rows={3}
                      value={form.notes}
                      onChange={(e) => set("notes", e.target.value)}
                      className="input-ic resize-none"
                      placeholder="Gate code, pets, specific requests…"
                      data-testid="booking-notes"
                    />
                  </Field>
                </div>

                <div className="md:col-span-2">
                  <Field
                    label={
                      <>
                        Photos of your yard / work
                        <span className="text-muted-foreground normal-case tracking-normal ml-2 text-[11px]">
                          optional · up to {MAX_PHOTOS} · 4MB each
                        </span>
                      </>
                    }
                  >
                    <div className="border border-dashed border-border p-4 bg-background" data-testid="booking-photos">
                      {photos.length > 0 && (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mb-3">
                          {photos.map((p, i) => (
                            <div
                              key={i}
                              className="relative aspect-square border border-border overflow-hidden group"
                              data-testid={`booking-photo-thumb-${i}`}
                            >
                              <img
                                src={p.dataUrl}
                                alt={p.name}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removePhoto(i)}
                                className="absolute top-1 right-1 bg-black/70 border border-border p-1 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                aria-label={`Remove ${p.name}`}
                                data-testid={`booking-photo-remove-${i}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <label
                        htmlFor="booking-photo-input"
                        className={`flex items-center justify-center gap-2 border border-border py-3 text-xs uppercase tracking-widest cursor-pointer transition-colors ${
                          photos.length >= MAX_PHOTOS
                            ? "opacity-40 cursor-not-allowed"
                            : "hover:border-primary hover:text-primary"
                        }`}
                      >
                        <Upload className="h-4 w-4" />
                        {photos.length === 0
                          ? "Add photos"
                          : photos.length >= MAX_PHOTOS
                          ? "Max reached"
                          : `Add more (${photos.length}/${MAX_PHOTOS})`}
                      </label>
                      <input
                        id="booking-photo-input"
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={onPhotoSelect}
                        disabled={photos.length >= MAX_PHOTOS}
                        data-testid="booking-photo-input"
                      />
                      <p className="text-[11px] text-muted-foreground mt-2">
                        Help us quote faster — upload pictures of the yard or the work
                        you'd like done.
                      </p>
                    </div>
                  </Field>
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={form.is_veteran}
                      onChange={(e) => set("is_veteran", e.target.checked)}
                      className="w-4 h-4 accent-[hsl(101,32%,55%)]"
                      data-testid="booking-veteran-check"
                    />
                    <span className="text-sm text-muted-foreground">
                      I'm a veteran — apply 10% discount
                    </span>
                  </label>
                </div>

                <div className="md:col-span-2">
                  <Field
                    label={
                      <>
                        Preferred Date <CalendarIcon className="inline h-3.5 w-3.5 ml-1 -mt-0.5 text-primary" />
                      </>
                    }
                  >
                    <div className="border border-border p-3 bg-background overflow-x-auto" data-testid="booking-calendar">
                      <DayPicker
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={[
                          { before: new Date() },
                          ...fullDates.map((d) => new Date(d + "T00:00")),
                        ]}
                        className="mx-auto"
                      />
                    </div>
                    {date && (
                      <div className="mt-2 text-sm text-primary" data-testid="booking-selected-date">
                        Selected: {date.toDateString()}
                      </div>
                    )}
                  </Field>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full mt-8 disabled:opacity-60"
                data-testid="booking-submit"
              >
                {submitting ? (
                  "Submitting…"
                ) : (
                  <>
                    <Check className="h-4 w-4" /> Request Estimate
                  </>
                )}
              </button>
              <p className="text-xs text-muted-foreground mt-4 text-center">
                No payment required. We'll confirm by phone within 24 hours.
              </p>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .input-ic {
          width: 100%;
          background: hsl(var(--background));
          border: 1px solid hsl(var(--border));
          color: hsl(var(--foreground));
          padding: 12px 14px;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 0.2s;
          font-family: inherit;
        }
        .input-ic:focus {
          border-color: hsl(var(--primary));
        }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
